/**
 * The four procedures Martyn's Law names, their labels, and the built-in
 * default instruction for each — the single source of truth for both.
 *
 * The instruction is not decoration on the procedure; it is the opposite of it
 * in two of the four cases. "Leave the building by the nearest exit" is the
 * right words for an evacuation and the wrong words for a lockdown, where
 * movement is the danger (`dashboard-requirements.md`).
 *
 * These are the *built-in* defaults. A building may store its own instruction
 * per procedure (migration 0093, `site_procedures`), and the raise flow prefers
 * a building's stored words over the built-in default when one exists — but the
 * building falls back to these when it has written none, so this map is what the
 * procedures editor shows as the placeholder and what the raise flow lands on
 * for a building that has never customised the wording.
 */

export type Procedure = 'evacuation' | 'invacuation' | 'lockdown' | 'communication';

export interface ProcedureDef {
  value: Procedure;
  label: string;
  /** The built-in default wording, before any per-building override. */
  instruction: string;
}

export const PROCEDURES: ProcedureDef[] = [
  {
    value: 'evacuation',
    label: 'Evacuation',
    instruction: 'Leave the building by the nearest exit.',
  },
  {
    value: 'invacuation',
    label: 'Invacuation',
    instruction: 'Come inside now and move away from the windows.',
  },
  {
    value: 'lockdown',
    label: 'Lockdown',
    instruction: 'Lock the door, stay away from windows, and keep quiet.',
  },
  {
    value: 'communication',
    label: 'Communication',
    instruction: '',
  },
];

/** The built-in default instruction for one procedure ('' for communication). */
export function defaultInstruction(procedure: Procedure): string {
  return PROCEDURES.find((p) => p.value === procedure)?.instruction ?? '';
}

/**
 * One shape of a building's stored procedure row (migration 0093): the words
 * the building has written, or `null` where it has written none. Kept structural
 * (not the `api.SiteProcedure` type) so this module carries no dependency on the
 * API layer.
 */
export interface StoredProcedure {
  procedure: Procedure;
  instruction: string | null;
}

/**
 * Resolve the default instruction for a procedure, preferring a building's own
 * stored words (migration 0093) over the built-in default.
 *
 * This is the single resolution used by BOTH the raise flow and the
 * mid-incident procedure transition, so the two can never drift: escalating
 * evacuation→lockdown mid-incident must land on the building's own lockdown
 * wording, not the generic built-in line — at the highest-stakes moment the
 * feature exists for. A row with a `null` (or absent) instruction means the
 * building has not customised that procedure, so the built-in default applies.
 */
export function resolveStoredInstruction(
  procedure: Procedure,
  stored: readonly StoredProcedure[] | undefined,
): string {
  const words = stored?.find((x) => x.procedure === procedure)?.instruction;
  return words ?? defaultInstruction(procedure);
}

/** The human label for one procedure. */
export function procedureLabel(procedure: Procedure): string {
  return PROCEDURES.find((p) => p.value === procedure)?.label ?? procedure;
}
